import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Link,
  Alert,
  Typography,
} from '@mui/material';
import { PublicLayout } from '../layouts/PublicLayout';
import { useAuth } from '../hooks/useAuth';

interface FormErrors {
  email?: string;
  password?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const from = (location.state as { from?: Location })?.from?.pathname || '/projects';

  // 既にログイン済みの場合はリダイレクト
  if (isAuthenticated) {
    navigate(from, { replace: true });
  }

  // バリデーション
  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};

    if (!email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    if (!password) {
      errors.password = 'パスワードを入力してください';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password]);

  // エラーメッセージをユーザーフレンドリーに変換
  const getErrorMessage = useCallback((err: unknown): string => {
    if (err instanceof Error) {
      const message = err.message.toLowerCase();

      // 認証エラー
      if (message.includes('unauthorized') || message.includes('認証') || message.includes('invalid credentials')) {
        return 'メールアドレスまたはパスワードが正しくありません';
      }

      // アカウント関連
      if (message.includes('inactive') || message.includes('無効')) {
        return 'このアカウントは無効化されています。管理者にお問い合わせください';
      }

      // ネットワークエラー
      if (message.includes('network') || message.includes('ネットワーク') || message.includes('fetch')) {
        return 'サーバーに接続できません。ネットワーク接続を確認してください';
      }

      // タイムアウト
      if (message.includes('timeout')) {
        return 'サーバーからの応答がありません。しばらく待ってから再度お試しください';
      }

      // その他のエラー（サーバーからのメッセージをそのまま表示）
      return err.message;
    }

    return 'ログインに失敗しました。しばらく待ってから再度お試しください';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // バリデーション
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await login({ email, password, rememberMe });
      navigate(from, { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout title="ログイン">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="メールアドレス"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldErrors.email) {
              setFieldErrors(prev => ({ ...prev, email: undefined }));
            }
          }}
          error={!!fieldErrors.email}
          helperText={fieldErrors.email}
          disabled={isLoading}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="パスワード"
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (fieldErrors.password) {
              setFieldErrors(prev => ({ ...prev, password: undefined }));
            }
          }}
          error={!!fieldErrors.password}
          helperText={fieldErrors.password}
          disabled={isLoading}
        />

        <FormControlLabel
          control={
            <Checkbox
              value="remember"
              color="primary"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
          }
          label="ログイン状態を保持"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={isLoading}
        >
          {isLoading ? 'ログイン中...' : 'ログイン'}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => navigate('/reset-password')}
          >
            パスワードをお忘れですか？
          </Link>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            テストアカウント
          </Typography>
          <Typography variant="caption" display="block">
            一般ユーザー: test@example.com / Test1234!
          </Typography>
          <Typography variant="caption" display="block">
            管理者: admin@example.com / Admin1234!
          </Typography>
        </Box>
      </Box>
    </PublicLayout>
  );
}
